import type { TableRowSeverity, TableRowSeverityStyle, TagColor } from '@boqnet/ui';
import type { DataTableColumn } from '@/types/components/data-table.types';
import i18n from '@/plugins/vue-i18n';
import {
  BULK_ORDER_STATUSES,
  type BulkOrderListItem,
  type BulkOrderStatus,
  type BulkOrderStatusFacetCell,
  type BulkOrdersFilter,
} from '@/types/api/bulk-orders.types';
import type { OrderParentBulkOrder } from '@/types/api/orders.types';
import type { BulkOrderFilters } from '@/types/data/filters.types';

const { t } = i18n.global;

/**
 * Status → the platform's `Tag` palette, by name.
 *
 * Context: the colours come from the platform's status palette rather than being chosen for this
 * feature (S-01) — awaiting approval borrows the `Requested` orange, the delivery ladder reads
 * green, and the terminated branches read red. The three **in-flight** states front a
 * multi-transaction fan-out and are normally sub-second, so they read `blue`: informational rather
 * than either "needs you" or "finished". `Approving` postdates the design pack (DL-66) and is
 * coloured as the sibling of `Placing` that it is. `Returned` is grey, as the platform's is.
 */
const STATUS_TAG_COLOUR: Record<BulkOrderStatus, TagColor> = {
  Placing: 'blue',
  AwaitingApproval: 'orange',
  Approving: 'blue',
  Approved: 'green',
  Rejected: 'red',
  Cancelling: 'blue',
  Cancelled: 'red',
  PartiallyDelivered: 'green',
  Delivered: 'green',
  Returned: 'gray',
};

export const bulkOrderStatusColour = (status: BulkOrderStatus): TagColor =>
  STATUS_TAG_COLOUR[status] ?? 'gray';

export const bulkOrderStatusLabel = (status: BulkOrderStatus): string =>
  t(`orders.bulkOrders.statuses.${status}`);

export const isBulkOrderStatus = (value: string): value is BulkOrderStatus =>
  (BULK_ORDER_STATUSES as readonly string[]).includes(value);

/**
 * How a row held up by a gating variation is drawn, on the Bulk Orders tab and on the hub's linked
 * sub-orders alike, so the two cannot drift (S-07 "Blocked treatment is consistent").
 *
 * Context: `edge` + `fill` is S-01's faint fill with a darker left border, and `warn` means "needs
 * attention", not "failed". A non-gating variation never marks a row blocked.
 */
export const BLOCKED_ROW_SEVERITY_STYLE: TableRowSeverityStyle[] = ['edge', 'fill'];

export const blockedRowSeverity = (hasBlockingVariation: boolean): TableRowSeverity | undefined =>
  hasBlockingVariation ? 'warn' : undefined;

/**
 * The same treatment on a Bulk Orders row whose run stopped part-way: the owner has a Retry waiting
 * on the hub (PE-1041, PE-1050).
 *
 * Context: a decline or cancellation is `Cancelling`, which the status alone says. A delivery is not
 * a status (it leaves the parent `Approved` or `PartiallyDelivered`), so the row's own
 * `hasStoppedRun` is what carries it, and any other kind that stopped as well.
 */
export const stoppedRunRowSeverity = (
  status: BulkOrderStatus,
  hasStoppedRun = false,
): TableRowSeverity | undefined => (hasStoppedRun || status === 'Cancelling' ? 'warn' : undefined);

/**
 * Which `orders.bulkOrders` tooltip says why a stopped row is flagged, or `undefined` for a row
 * that is not. A row carries no run kind, only its status and whether a run stopped, and the status
 * names the kind: a run that stops leaves `Placing`, `Approving` or `Cancelling` where it was, and a
 * delivery leaves the parent `Approved` or `PartiallyDelivered`.
 */
export const stoppedRunTooltipKey = (
  row: Pick<BulkOrderListItem, 'status' | 'hasStoppedRun'>,
):
  | 'stoppedTooltip'
  | 'stoppedPlacementTooltip'
  | 'stoppedApprovalTooltip'
  | 'stoppedDeliveryTooltip'
  | undefined => {
  if (row.status === 'Cancelling') return 'stoppedTooltip';
  if (!row.hasStoppedRun) return undefined;
  if (row.status === 'Placing') return 'stoppedPlacementTooltip';
  return row.status === 'Approving' ? 'stoppedApprovalTooltip' : 'stoppedDeliveryTooltip';
};

/**
 * The pulse that points at rows the user just caused: the ones Allocate's fill changed (PE-679,
 * PE-681), and the bulk orders a placement created when it lands on the list. **Colour only,
 * roughly 1.5s, no movement and no scaling.**
 *
 * One static string, because the keyframe itself is declared in the app's `index.css`.
 */
export const ROW_FLASH_CLASS = 'bulk-row-flash';

/**
How long {@link ROW_FLASH_CLASS} runs, so a caller can take the class off once it has played.
*/
export const ROW_FLASH_MS = 1500;

/**
 * The ten statuses as multi-select options, in lifecycle order.
 *
 * Deliberately no `color`: a coloured dot per row was tried and rejected at review — ten of them
 * read as decoration rather than a key, and the status colour is already carried by the chip in
 * the row the filter selects. `bulkOrderStatusColour` remains the single source for that chip.
 */
export const bulkOrderStatusOptions = (): { value: BulkOrderStatus; label: string }[] =>
  (Object.keys(STATUS_TAG_COLOUR) as BulkOrderStatus[]).map((status) => ({
    value: status,
    label: bulkOrderStatusLabel(status),
  }));

/**
 * The list's Vendor notified column, on the desktop table only: the phone cards show nothing for
 * it, as the Orders list's cards do not (DL-261 item 4).
 */
export const VENDOR_NOTIFIED_COLUMN = 'vendorNotified';

/**
 * The parent list's columns.
 *
 * Shared by the desktop table and the mobile card list (PE-582): the table renders them as
 * headers and the card renders them as field labels, so a single definition is what stops the
 * two naming the same value differently.
 */
export const bulkOrderListColumns = (): DataTableColumn[] => [
  { field: 'reference', header: t('orders.bulkOrders.headers.reference') },
  { field: 'project', header: t('orders.bulkOrders.headers.project') },
  { field: 'vendorName', header: t('orders.bulkOrders.headers.vendor') },
  { field: 'status', header: t('orders.bulkOrders.headers.status') },
  { field: 'siteCount', header: t('orders.bulkOrders.headers.siteCount') },
  { field: 'createdAt', header: t('orders.bulkOrders.headers.placed') },
  // Before the money, as the Orders list sets its own beside its amounts (PE-1349).
  { field: VENDOR_NOTIFIED_COLUMN, header: t('orders.bulkOrders.headers.vendorNotified') },
  { field: 'total', header: t('orders.bulkOrders.headers.total') },
  { field: 'actions', header: t('general.actions') },
];

/**
 * The summary tiles — three fixed tiles in every lifecycle state (DL-60).
 *
 * Context: a tile is a **preset over the table's own controls**, not a second filtering path
 * (DL-35). Clicking one sets the status multi-select and the blocked dimension that already exist,
 * which is why an aggregating tile like *In delivery* costs nothing: `status` takes an array.
 */
export type BulkOrderTileKey = 'readyToApprove' | 'blockedByVariation' | 'inDelivery';

export interface BulkOrderTileDefinition {
  key: BulkOrderTileKey;
  /**
  Statuses this tile filters to, and sums its count over.
  */
  statuses: BulkOrderStatus[];
  /**
   * The blocked dimension this tile pins, or `undefined` to leave it unfiltered.
   *
   * Context: blocked is a row property with its own field and its own filter parameter, never a
   * status value (S-01, DL-35). Adding it to the status enum would corrupt the state machine.
   */
  hasBlockingVariation?: boolean;
  iconColour: 'orange' | 'red' | 'blue';
}

export const BULK_ORDER_TILES: BulkOrderTileDefinition[] = [
  {
    key: 'readyToApprove',
    statuses: ['AwaitingApproval'],
    hasBlockingVariation: false,
    iconColour: 'orange',
  },
  {
    key: 'blockedByVariation',
    statuses: ['AwaitingApproval'],
    hasBlockingVariation: true,
    iconColour: 'red',
  },
  {
    key: 'inDelivery',
    statuses: ['Approved', 'PartiallyDelivered'],
    iconColour: 'blue',
  },
];

/**
 * The filter a tile applies, merged onto the filter already in place so the shared dimensions
 * (search, vendor, project, dates) survive activating a tile.
 */
export const tileFilter = (
  tile: BulkOrderTileDefinition,
  current: BulkOrdersFilter,
): BulkOrdersFilter => ({
  ...current,
  status: [...tile.statuses],
  hasBlockingVariation: tile.hasBlockingVariation,
});

/**
Whether `filter` is exactly what `tile` would apply — what drives the tile's pressed state.
*/
export const isTileActive = (tile: BulkOrderTileDefinition, filter: BulkOrdersFilter): boolean => {
  const statuses = filter.status ?? [];

  return (
    statuses.length === tile.statuses.length &&
    tile.statuses.every((status) => statuses.includes(status)) &&
    filter.hasBlockingVariation === tile.hasBlockingVariation
  );
};

/**
Clears only the two dimensions a tile owns, leaving the rest of the filter alone.
*/
export const clearTileFilter = (current: BulkOrdersFilter): BulkOrdersFilter => {
  const { status: _status, hasBlockingVariation: _blocked, ...rest } = current;
  return rest;
};

/**
 * The statuses the Bulk orders tab badge counts, across both blocked states.
 *
 * Context: mirrors the Orders badge, which counts `Requested` orders only (DL-189 P-06).
 */
export const BULK_ORDER_BADGE_STATUSES: readonly BulkOrderStatus[] = ['AwaitingApproval'];

/**
The badge's figure from an unfiltered cross-tab. A status with no rows has no cell: adds 0.
*/
export const bulkOrderBadgeCount = (cells: BulkOrderStatusFacetCell[]): number =>
  cells
    .filter((cell) => BULK_ORDER_BADGE_STATUSES.includes(cell.status))
    .reduce((sum, cell) => sum + cell.count, 0);

/**
 * The persisted, query-shaped filter → the typed filter the API takes.
 *
 * Context: the values come from a URL, so a hand-edited or stale link can carry a status the enum
 * no longer has; those are dropped rather than sent, since the list endpoint would reject the whole
 * request for one unknown value.
 */
export const toBulkOrdersFilter = (persisted: BulkOrderFilters): BulkOrdersFilter => {
  const status = (persisted.bulkStatus ?? '').split(',').filter(isBulkOrderStatus);

  return {
    reference: persisted.bulkSearch || undefined,
    projectId: persisted.bulkProject || undefined,
    vendorId: persisted.bulkVendor || undefined,
    startDate: persisted.bulkStartDate || undefined,
    endDate: persisted.bulkEndDate || undefined,
    status: status.length > 0 ? status : undefined,
    hasBlockingVariation:
      persisted.bulkBlocked === 'true'
        ? true
        : persisted.bulkBlocked === 'false'
          ? false
          : undefined,
  };
};

/**
 * The typed filter → every persisted key, cleared ones included. `updateFilters` merges its patch,
 * so a key left out would keep its old value rather than clear.
 */
export const toPersistedBulkFilter = (
  filter: BulkOrdersFilter,
): Record<keyof BulkOrderFilters, string | undefined> => ({
  bulkSearch: filter.reference || undefined,
  bulkProject: filter.projectId || undefined,
  bulkVendor: filter.vendorId || undefined,
  bulkStatus: filter.status?.length ? filter.status.join(',') : undefined,
  bulkBlocked:
    filter.hasBlockingVariation === undefined ? undefined : String(filter.hasBlockingVariation),
  bulkStartDate: filter.startDate || undefined,
  bulkEndDate: filter.endDate || undefined,
});

/**
 * Whether an order is a bulk CHILD, and therefore which actions it loses.
 *
 * The lifecycle four it never offers are **approve, decline, cancel and complete**: approval
 * cascades from the parent in one action and a child carries none of its own (FR-5, FR-18, product
 * decision of 2026-08-04); there is no per-child cancel at all, which protects the committed vendor
 * PO and the MOQ aggregate (FR-27); and completing an under-delivered child is the owner's,
 * performed from the parent hub (FR-31, S-07.2).
 *
 * It does not offer **Log a delivery** either, on any surface: the owner logs every bulk delivery
 * once, on the parent, and the API refuses one posted against a child (ADR-017, PE-1039). **Issue a
 * return it keeps** (FR-34), so that one is deliberately not withheld.
 *
 * Context: asked WITHOUT `erp_app_bulk_orders_v1`, deliberately. The flag gates bulk's own
 * surfaces — a band naming a feature nobody has been given is noise — but this withholds
 * controls, and turning the flag off once children exist is a rollback. A rollback that hands a
 * site the authority the cascade owns is a worse failure than whatever it was undoing.
 *
 * Lives here rather than being re-derived per surface because it is asked by four of them (the
 * shared and reworked drawer bodies, both drawer headers, and the orders-list row menu), and the
 * four had already drifted: cancel was guarded on one and nothing was guarded on the others.
 */
export const isBulkChildOrder = (
  order?: { parentBulkOrder?: OrderParentBulkOrder } | null,
): boolean => !!order?.parentBulkOrder;

const EMPTY_GUID = '00000000-0000-0000-0000-000000000000';

/**
 * The project a bulk child's parent belongs to, or `null` where the link does not say.
 *
 * Context: a site order placed before the link carried its project reads the empty GUID (dev and
 * staging only; the backend does not backfill). Such a row may not link its bulk order, rather
 * than be asked unscoped and offer a hub that may refuse the viewer (PE-1221). The guard can go
 * once dev and staging hold no such row.
 */
export const parentBulkOrderProjectId = (
  parent: Pick<OrderParentBulkOrder, 'projectId'>,
): string | null => (parent.projectId && parent.projectId !== EMPTY_GUID ? parent.projectId : null);

type SiteOrderDocument = { documentType?: string; sourceEntity?: string; sourceEntityId?: string };

/**
 * What a bulk order's site order may upload and remove (FR-61, DL-203, DL-219): the same rule
 * PE-635's server applies, mirrored so the common case never meets its refusal.
 */
export type BulkSiteOrderDocumentRules = {
  /**
  Each document type the site order may not upload, and the line its row shows instead.
  */
  withheldUploads: Record<string, string>;
  canDelete: (document: SiteOrderDocument) => boolean;
};

/**
 * Read from the documents the server serves on the site order, never from a list of types, so a
 * bulk order that holds no tax invoice yet withholds none. `null` for any other order.
 *
 * Context: a document the site order did not upload reached it through the bulk order (its own,
 * its payment request's or a sibling's), so its type is held and it may only be removed there. A
 * site order never takes a delivery note: deliveries are logged once, on the bulk order (DL-219).
 * Keyed on the bulk order link and not the flag, for the reason `isBulkChildOrder` gives.
 */
export const bulkSiteOrderDocumentRules = (
  order?: {
    id?: string;
    parentBulkOrder?: Pick<OrderParentBulkOrder, 'reference'>;
    documents?: SiteOrderDocument[];
  } | null,
): BulkSiteOrderDocumentRules | null => {
  const parent = order?.parentBulkOrder;
  if (!parent) return null;

  const isOriginated = (document: SiteOrderDocument): boolean =>
    document.sourceEntity === 'Order' && document.sourceEntityId === order.id;

  const heldLine = t('orders.bulkOrders.child.heldOnBulkOrder', { reference: parent.reference });
  const withheldUploads: Record<string, string> = {
    GoodsReceivedNote: t('orders.bulkOrders.child.deliveryNotesOnBulkOrder', {
      reference: parent.reference,
    }),
  };

  for (const document of order.documents ?? []) {
    if (document.documentType && document.documentType !== 'Other' && !isOriginated(document)) {
      withheldUploads[document.documentType] = heldLine;
    }
  }

  return { withheldUploads, canDelete: isOriginated };
};

// `Returned` too: a return needs an approved site order, so the bulk order was approved (DL-211).
const POST_APPROVAL_STATUSES = new Set<BulkOrderStatus>([
  'Approved',
  'PartiallyDelivered',
  'Delivered',
  'Returned',
]);

const PRE_APPROVAL_DOCUMENT_TYPES = new Set(['Quote', 'Invoice']);

/**
 * Whether a bulk order in `status` shows a document of `documentType` in Key documentation. The
 * single gate, so the hub's GRN action (PE-1049) asks here rather than deriving its own.
 *
 * Context: post-approval documents only make sense after approval, as on an order (PE-979). Display
 * only, and frontend-only because the engine's bulk rule set is kept identical to an order's (PE-643).
 */
export const isDocumentTypeInScopeForBulkOrder = (
  documentType: string,
  status: BulkOrderStatus,
): boolean => POST_APPROVAL_STATUSES.has(status) || PRE_APPROVAL_DOCUMENT_TYPES.has(documentType);

/**
 * Which kind of order a vendor's portion becomes (FR-4): fewer than two sites is a standard order.
 *
 * ⚠️ A classification, and the server settles it at placement (`orders[].kind`). Held in one place
 * because two readers need it: Review says what will be created, and the wizard store checks that
 * the payment details step 4 confirmed were for the same kinds of order (PE-981).
 */
export type ResultingOrderKind = 'bulkOrder' | 'standardOrder';

/**
The minimum sites a bulk order covers (FR-4). Below it, the portion falls back to standard.
*/
export const BULK_ORDER_MINIMUM_SITES = 2;

export const resultingOrderKind = (siteCount: number): ResultingOrderKind =>
  siteCount < BULK_ORDER_MINIMUM_SITES ? 'standardOrder' : 'bulkOrder';

const ENDED_SITE_ORDER_STATUSES: ReadonlySet<string> = new Set([
  'Cancelled',
  'Rejected',
  'Returned',
]);

/**
 * Whether a bulk order's site order is still live: it has not ended Cancelled, Rejected or Returned,
 * as the API's `noChildOrdersRemain` counts.
 */
export const isLiveSiteOrder = (child: { status: string }): boolean =>
  !ENDED_SITE_ORDER_STATUSES.has(child.status);
